import React, { PureComponent } from 'react';
import classNames from 'classnames';
import { FormattedDataPoint } from '../features/device';
import { getUserUnitI } from '../common/ambient';
import PropTypes from 'prop-types';
import { range, path } from 'ramda';
import { ResizeObserver } from '@juggle/resize-observer';
import {
  DayForecast
} from './';
import bindUserActions from '../features/user/redux/bindUserActions';
import { getHourlyForecastLimit } from '../features/payment';


class ForecastLongTerm extends PureComponent {
  static propTypes = {
    forecast: PropTypes.object,
    user: PropTypes.object.isRequired,
    expanded: PropTypes.bool,
    now: PropTypes.number
  }

  state = {
    currentDate: null
  }

  _ref (ref) {
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver((entries) => {
        this.setState({
          width: entries[0].contentRect.width,
          height: entries[0].contentRect.height,
        })
      })
      this.resizeObserver.observe(ref);
    }
  }

  _setupScrollTracking (containerRef) {
    if (!containerRef || this.scrollTrackingSetup) return;
    this.scrollTrackingSetup = true;
    
    // Track scroll and update the date based on what's currently visible
    this._updateStickyDate = () => {
      const containerRect = containerRef.getBoundingClientRect();
      const newDayElements = containerRef.querySelectorAll('.hour.new-day');
      
      let currentDate = null;
      let lastDateBeforeView = null;
      let isAltDay = false;
      
      // Find the last date marker that's to the left of or at the start of the viewport
      newDayElements.forEach(el => {
        const rect = el.getBoundingClientRect();
        const date = el.getAttribute('data-date');
        
        if (rect.left <= containerRect.left + 5) {
          // This date is at or has scrolled past the left edge
          lastDateBeforeView = date;
          // Check if this day has the alt class
          isAltDay = el.classList.contains('alt');
        }
      });
      
      // Only show sticky date if there's a date that has scrolled off
      if (lastDateBeforeView && containerRef.scrollLeft > 0) {
        currentDate = lastDateBeforeView;
      }
      
      if (this.state.currentDate !== currentDate || this.state.currentDateIsAlt !== isAltDay) {
        this.setState({ currentDate, currentDateIsAlt: isAltDay });
      }
    };
    
    // Listen to scroll events
    containerRef.addEventListener('scroll', this._updateStickyDate);
    
    // Initial check
    setTimeout(this._updateStickyDate, 100);
  }

  _handleScroll = (e) => {
    // This is now handled by _updateStickyDate
  }

  componentWillUnmount () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.hourlyWrapRef && this._updateStickyDate) {
      this.hourlyWrapRef.removeEventListener('scroll', this._updateStickyDate);
    }
  }

  render () {
    const { now, onClose, user, forecast, expanded } = this.props
    const { width } = this.state
    if (!forecast) return null
    const { currently = {}, hourly, daily = {} } = forecast
    const days = (daily.data || []).filter(f => moment(f.time * 1000).startOf('day').valueOf() >= moment(now).startOf('day').valueOf())
    const today = days[0]
    let daysToShow = 5
    let hourlyShow
    if (expanded) {
      daysToShow = 10
      let lastDay = moment(currently.time * 1000).dayOfYear()
      let newDays = 0
      const hourlyData = (hourly && hourly.data) || []
      const combinedData = [currently, ...hourlyData].slice(0, getHourlyForecastLimit(user))
      hourlyShow = (
        <div className='hourly-container'>
          {this.state.currentDate && (
            <div className={classNames('sticky-date-indicator', { alt: this.state.currentDateIsAlt })}>{this.state.currentDate}</div>
          )}
          <div className='hourly-wrap' ref={ref => {
            this.hourlyWrapRef = ref;
            if (ref) {
              setTimeout(() => this._setupScrollTracking(ref), 100);
            }
          }}>
            <div className={classNames('hourly', `limit-${getHourlyForecastLimit(user)}`)}>{combinedData.map((hour, i) => {
          if (!hour) return null; // Skip if hour data is missing

          const m = moment(hour.time * 1000);
          let title = i === 0 ? 'Now' : m.format(getUserUnitI('hour24', user) === 1 ? 'H' : 'h');
          let cls = ''

          if (lastDay !== m.dayOfYear()) {
            lastDay = m.dayOfYear();
            title = m.format('M/D');
            cls = 'new-day';
            newDays++;
          }
          return (
            <div key={`hour-${i}`} className={classNames('hour', { alt: newDays % 2 === 1 }, cls)} data-date={cls === 'new-day' ? title : null}>
              <div className='title'>{title}</div>
              <div className={classNames('weather-icon', hour.icon)} />
              <FormattedDataPoint type='tempf' value={hour.temperature} after='°' valueTransform={Math.round} />
            </div>
          )
        })}</div>
          </div>
        </div>
      )
    }
    const otherDays = days.slice(1, 1 + daysToShow)
    return (
      <div
        ref={this._ref.bind(this)}
        className={classNames('device-forecast-widget component-forecast-long-term', {
          md: width >= 260,
          lg: width >= 320,
          'has-hourly': expanded
        })}
      >
        <a className={classNames('close', { 'on-close': onClose })} onClick={onClose} />
        <div className='forecast-text'>
          <FormattedDataPoint type='tempf' value={currently.temperature} after='°' />
          &nbsp;
          <span>{path(['minutely', 'summary'], forecast)} </span>
          <span>{path(['hourly', 'summary'], forecast)} </span>
        </div>
        <DayForecast
          className='today'
          title='Today'
          forecast={today}
        />
        {hourlyShow}
        <div className='other-days'>
          {otherDays.map(f => (
            <DayForecast
              key={`forecast-${f.time}`}
              title={moment(f.time * 1000).format('dd').slice(0, 1)}
              forecast={f}
            />))
          }
        </div>
        <p>
          Weather forecast by <span className='accuweather-logo' /> 
        </p>
      </div>
    )
  }
}

export default bindUserActions(ForecastLongTerm)
ForecastLongTerm.displayName = 'ForecastLongTerm'
